import config from 'config'
import { getTabId } from 'util/tabId'
import { getVersion } from 'util/version'
import { selectIsRefreshingToken, selectAccountSubdomain } from 'selectors/app'
import { selectAccountSubdomainFromLocation } from 'selectors/location'
import { LOGOUT_PAGE } from 'constants/pages'
import {
  TOKEN_REFRESH_REQUEST,
  TOKEN_REFRESH_SUCCESS,
  TOKEN_REFRESH_FAILURE,
} from 'constants/action_types'
import grooveAPI from 'api/groove'
import storage from 'util/storage'
import Bugsnag from '@bugsnag/js'

const reportToBugsnag = message => {
  try {
    Bugsnag.notify(new Error(message), event => {
      // eslint-disable-next-line no-param-reassign
      event.severity = 'info'
    })
  } catch (e) {
    // eslint-disable-next-line no-console
    console.debug('Failed to report to Bugsnag', e)
    Bugsnag.notify(e)
  }
}

const isExpectedTransportError = e =>
  e.name === 'AbortError' ||
  (e.name === 'TypeError' && /fetch|network|load failed/i.test(e.message))

const AUTH_REFRESH_LOCK_POLL_MS = 150
const TOKEN_REFRESH_TIMEOUT_MS = 30 * 1000
export const AUTH_REFRESH_LOCK_KEY = 'authRefreshLock'
const AUTH_REFRESH_LOCK_TTL_MS = TOKEN_REFRESH_TIMEOUT_MS + 10 * 1000
const AUTH_REFRESH_LOCK_WAIT_MS = AUTH_REFRESH_LOCK_TTL_MS + 5 * 1000
export const getAuthRefreshLockKey = subdomain =>
  `${AUTH_REFRESH_LOCK_KEY}:${subdomain || 'default'}`

const delay = ms => new Promise(resolve => setTimeout(resolve, ms))
const jitter = () => Math.floor(Math.random() * 50)

const isAuthFresh = (auth, timeOffset = null) => {
  if (!auth?.token || !auth?.expiresAt) return false
  const timestamp = timeOffset || Math.floor(Date.now() / 1000 + 1 * 60 * 30)
  return auth.expiresAt >= timestamp
}

const readRefreshLock = subdomain => {
  try {
    const lock = storage.get(getAuthRefreshLockKey(subdomain))
    if (!lock?.ownerId || !lock?.expiresAtMs) return null
    if (lock.expiresAtMs <= Date.now()) return null
    return lock
  } catch (e) {
    return null
  }
}

const writeRefreshLock = (ownerId, subdomain) => {
  try {
    const startedAt = Date.now()
    storage.set(getAuthRefreshLockKey(subdomain), {
      ownerId,
      subdomain,
      startedAt,
      expiresAtMs: startedAt + AUTH_REFRESH_LOCK_TTL_MS,
    })
    return true
  } catch (e) {
    return false
  }
}

export const releaseAuthRefreshLock = (ownerId, subdomain) => {
  try {
    const lockKey = getAuthRefreshLockKey(subdomain)
    const lock = storage.get(lockKey)
    if (lock?.ownerId === ownerId) {
      storage.remove(lockKey)
    }
  } catch (e) {
    // localStorage unavailable; nothing to release
  }
}

export const waitForAuthRefreshLock = async ({
  originalToken,
  subdomain,
  timeOffset = null,
  maxWaitMs = AUTH_REFRESH_LOCK_WAIT_MS,
  deadlineMs = Date.now() + maxWaitMs,
} = {}) => {
  while (Date.now() < deadlineMs) {
    const auth = storage.get('auth') || {}
    if (!auth.token) return { authCleared: true, auth }
    if (auth.token !== originalToken || isAuthFresh(auth, timeOffset)) {
      return {
        authChanged: auth.token !== originalToken,
        authFresh: true,
        auth,
      }
    }
    if (!readRefreshLock(subdomain)) return { lockInactive: true, auth }
    // eslint-disable-next-line no-await-in-loop
    await delay(AUTH_REFRESH_LOCK_POLL_MS + jitter())
  }
  return { timedOut: true, auth: storage.get('auth') || {} }
}

const acquireAuthRefreshLock = async (ownerId, subdomain) => {
  const lock = readRefreshLock(subdomain)
  if (lock && lock.ownerId !== ownerId) return { acquired: false }
  if (!writeRefreshLock(ownerId, subdomain))
    return { acquired: true, lockUnavailable: true }
  await delay(20 + jitter())
  const confirmedLock = readRefreshLock(subdomain)
  if (!confirmedLock) return { acquired: true, lockUnavailable: true }
  return { acquired: confirmedLock.ownerId === ownerId }
}

const syncRefreshedAuth = (dispatch, auth) => {
  dispatch({
    type: TOKEN_REFRESH_SUCCESS,
    data: {
      token: auth.token,
      expiresAt: auth.expiresAt,
    },
  })
}

// NOTE: We hit this endpoint after a user as signed up in order to add the
// refresh token to the session.
export function doReloadTokenInfo() {
  return async dispatch => {
    const { token, previousToken } = storage.get('auth') || {}
    if (!token) return null

    const response = await grooveAPI.get(token, 'oauth/token/info', {})
    const { json } = response
    const { expires_in_seconds: expiresInSeconds } = json
    // If no expiresInSeconds response is returned by the server then don't
    // update the expiresAt in the store because that would immediately expire
    // the token and log the user out. This shouldn't happen but there may be
    // agents with weird local storage states right now. This whole PR can be
    // reverted next week.
    const expiresAt = expiresInSeconds
      ? Math.floor(new Date() / 1000 + expiresInSeconds)
      : null
    storage.set('auth', {
      authenticated: true,
      token,
      expiresAt,
      previousToken,
    })
    dispatch({
      type: TOKEN_REFRESH_SUCCESS,
      data: {
        token,
        expiresAt,
      },
    })
    return null
  }
}

export function doRefreshIfNecessary(timeOffset = null) {
  return async (dispatch, getState) => {
    // Skip token refresh if user is on logout page to prevent "Failed to fetch" errors
    if (window.location.pathname === '/logout') return null

    const { token, expiresAt } = storage.get('auth') || {}
    if (!token) return null
    if (!expiresAt) return null

    // Time Offset or 30 mins from now. Doing this because there seems to be
    // a race condition happening sometimes when this code is run before
    // requests. The time offset allows us to pass in a different value, so the
    // global setInterval running in the App component refreshes before the race
    // condition can happen.
    const timestamp = timeOffset || Math.floor(Date.now() / 1000 + 1 * 60 * 30)
    if (expiresAt >= timestamp) return null

    const state = getState()
    const isRefreshing = selectIsRefreshingToken(state)

    // Same-tab guard stays unchanged; cross-tab lock only coordinates other tabs.
    if (isRefreshing) return null

    // The account subdomain isn't in the store on first load, so fall back to
    // using the from location selector, which uses the URL.
    // There is an edge case in dev, where the url is mobile.groovehq.docker
    // which is not going to work either way.
    const subdomain =
      selectAccountSubdomain(state) || selectAccountSubdomainFromLocation(state)

    const ownerId = `${getTabId()}-${Math.random()
      .toString(36)
      .slice(2)}`
    const waitDeadlineMs = Date.now() + AUTH_REFRESH_LOCK_WAIT_MS
    const activeLock = readRefreshLock(subdomain)
    if (activeLock && activeLock.ownerId !== ownerId) {
      const waitResult = await waitForAuthRefreshLock({
        originalToken: token,
        subdomain,
        timeOffset,
        deadlineMs: waitDeadlineMs,
      })
      if (waitResult.authFresh && waitResult.auth?.token) {
        syncRefreshedAuth(dispatch, waitResult.auth)
        return null
      }
      if (waitResult.authCleared) return null
    }

    let lockResult = await acquireAuthRefreshLock(ownerId, subdomain)
    if (!lockResult.acquired) {
      const waitResult = await waitForAuthRefreshLock({
        originalToken: token,
        subdomain,
        timeOffset,
        deadlineMs: waitDeadlineMs,
      })
      if (waitResult.authFresh && waitResult.auth?.token) {
        syncRefreshedAuth(dispatch, waitResult.auth)
        return null
      }
      if (waitResult.lockInactive || waitResult.timedOut) {
        lockResult = await acquireAuthRefreshLock(ownerId, subdomain)
      }
      if (!lockResult.acquired) return null
    }
    const { lockUnavailable } = lockResult
    const releaseLock = () => {
      if (!lockUnavailable) releaseAuthRefreshLock(ownerId, subdomain)
    }
    const lockStillOwned = () =>
      lockUnavailable || readRefreshLock(subdomain)?.ownerId === ownerId
    let refreshTimeoutId
    try {
      const authBeforeRefresh = storage.get('auth') || {}
      if (!authBeforeRefresh.token || authBeforeRefresh.token !== token) {
        if (isAuthFresh(authBeforeRefresh, timeOffset)) {
          syncRefreshedAuth(dispatch, authBeforeRefresh)
        }
        return null
      }
      if (!lockStillOwned()) return null

      const url = `${config.api_url}/oauth/token`
      const headers = {
        'Content-Type': 'application/json',
        'X-Client-Tab-Id': getTabId(),
        'X-Client-App-Version': getVersion(),
      }
      const body = {
        grant_type: 'refresh_token',
        client_id: config.oauthClientId,
        subdomain,
      }
      const AbortControllerClass = window.AbortController
      const abortController = AbortControllerClass && new AbortControllerClass()
      const request = new Request(url, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: new Headers(headers),
        credentials: 'include',
        ...(abortController && { signal: abortController.signal }),
      })
      dispatch({ type: TOKEN_REFRESH_REQUEST })
      if (abortController) {
        refreshTimeoutId = setTimeout(() => {
          abortController.abort()
        }, TOKEN_REFRESH_TIMEOUT_MS)
      }
      const response = await fetch(request)
      let parsed
      try {
        parsed = await response.json()
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
          throw new TypeError('Invalid refresh token response')
        }
        if (
          parsed.access_token &&
          (typeof parsed.expires_in !== 'number' ||
            !Number.isFinite(parsed.expires_in) ||
            parsed.expires_in <= 0)
        ) {
          throw new TypeError('Invalid refresh token expiry')
        }
      } catch (e) {
        const latestAuth = storage.get('auth') || {}
        if (latestAuth.token && latestAuth.token !== token) {
          syncRefreshedAuth(dispatch, latestAuth)
          return null
        }
        storage.remove('auth')
        dispatch({ type: TOKEN_REFRESH_FAILURE })
        dispatch({ type: LOGOUT_PAGE })
        reportToBugsnag(`Failed to parse refresh token response: ${e.message}`)
        return null
      }
      const { access_token: newToken, expires_in: expiresIn } = parsed
      const newExpiresAt = Math.floor(new Date().getTime() / 1000 + expiresIn)
      if (newToken) {
        const latestAuth = storage.get('auth') || {}
        if (latestAuth.token === token) {
          if (!lockStillOwned()) {
            dispatch({ type: TOKEN_REFRESH_FAILURE })
            return null
          }
          storage.set('auth', {
            authenticated: true,
            token: newToken,
            expiresAt: newExpiresAt,
          })
          dispatch({
            type: TOKEN_REFRESH_SUCCESS,
            data: {
              token: newToken,
              expiresAt: newExpiresAt,
            },
          })
        } else if (latestAuth.token) {
          syncRefreshedAuth(dispatch, latestAuth)
        }
      } else {
        const latestAuth = storage.get('auth') || {}
        if (latestAuth.token && latestAuth.token !== token) {
          syncRefreshedAuth(dispatch, latestAuth)
          return parsed
        }
        reportToBugsnag(`Failed to refresh token: ${JSON.stringify(parsed)}`)
        dispatch({ type: LOGOUT_PAGE })
      }
      return parsed
    } catch (e) {
      const latestAuth = storage.get('auth') || {}
      if (latestAuth.token && latestAuth.token !== token) {
        syncRefreshedAuth(dispatch, latestAuth)
        return null
      }
      // Transport failure is not auth loss; a subsequent 401 proves that.
      dispatch({ type: TOKEN_REFRESH_FAILURE })
      if (!isExpectedTransportError(e)) {
        reportToBugsnag(`Failed to refresh token: ${e.message}`)
      }
      return null
    } finally {
      if (refreshTimeoutId) clearTimeout(refreshTimeoutId)
      releaseLock()
    }
  }
}
